import React, {
  useContext,
  useRef,
  useCallback,
  MutableRefObject,
  useState,
  useMemo,
  useEffect,
  useLayoutEffect,
} from 'react';
import classNames from 'classnames';
import { ContextProviderNode, EditCompleteType } from '@decisions/server/DecisionsFramework/Design/Properties';
import { getParentValidationSummary, hasInvalidPathValidation } from 'utils/ValidationUtils';
import {
  getMappingField,
  getMappingFieldPath,
  getMappingName,
  getChildPath,
  getBuildJoinArraysMappingValue,
  isBuildJoinArrayMapping,
} from 'components/property-editors/MappingTypeSelector/utils/MappingSelectorUtils';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import {
  getEditorConfigDisplayName,
  getEditorConfigValue,
  getEditorValues,
  getMappingEditorValues,
} from 'components/property-editors/utils/EditorConfigurationUtils';
import { PickButton } from 'components/buttons/PickButton/PickButton';
import { Callout } from 'components/layout/Callout';
import { HasOpenState } from 'hooks/HasOpenState';
import { ActionLinks } from 'components/ActionLinks/ActionLinks';
import { ValidationError } from 'components/inputs/Validation/ValidationError';
import { MappingRowExpandIcon } from '../MappingRowExpandIcon';
import { MappingRowDataType } from './../MappingRowDataType';
import { StepMappingEditor } from './StepMappingEditor';
import { IconButton } from 'components/buttons/IconButton';
import { InputMappingConstants } from 'components/property-editors/MappingConstants';
import {
  NodeTypes,
  PROVIDER_NODE_PATH_SEPARATOR,
  categoryIsMappingTypeSelector,
} from 'components/property-editors/utils/ProviderNodeUtils';
import {
  getMappedValueNode,
  getNodeIsInitiallyOpen,
  mappingTypeRendersInlineEditor,
  containsPropertyNode,
  getMappingValidationId,
  getConverterFlowSuggestionActions,
  getMappingElementId,
  updateMappingRowHeights,
  mappingTypeRendersInOverlay,
} from '../utils/MappingUtils';
import { StepMappedValueText } from './StepMappedValueText';
import {
  createContextForProperty,
  editComplete,
} from '@decisions/server/DecisionsFramework/Design/Properties/ContextProviderService';
import {
  draggingFromFlowPath,
  getDragTrulyStarted,
  getExpandedStepNodes,
  getSelectedStepNodeId,
  MappingPanelType,
  storeIdSelector,
  useMappingEditorStore,
  useRowIsHovered,
  useRowPartnerIsHovered,
} from '../MappingEditorStore';
import debounce from 'debounce';

export interface IStepMappingRowProps {
  contextNode: ContextProviderNode;
  path: string[];
  showRemoveIcon?: boolean;
}

/**
 *
 *
 * Copyright (c) 2021 Decisions All Rights Reserved
 */
export const getItemClassNames = (className: string, hasValidation = false) =>
  classNames(className, 'dp-mapping-row__label', {
    [`dp-mapping-row__label--error`]: hasValidation,
  });

export const getRowClassNames = (
  className: string,
  isNodeSelected = false,
  isNodeHovered = false,
  isConstant = false,
  showAllEditors = false
) =>
  classNames(`${className} dp-mapping-row`, {
    [`dp-mapping-row--selected`]: isNodeSelected,
    [`dp-mapping-row--hovered`]: !isNodeSelected && isNodeHovered,
    [`dp-mapping-row--constant`]: isConstant,
    [`dp-mapping-row--show-all-editors`]: isConstant && showAllEditors,
  });

const getIconClassNames = (canAddRow: boolean, canRemoveRow?: boolean, canEdit?: boolean) => {
  return classNames(`${STEP_MAPPING_CLASS_NAME}__icon`, {
    [`${STEP_MAPPING_CLASS_NAME}__icon--add`]: canAddRow,
    [`${STEP_MAPPING_CLASS_NAME}__icon--remove`]: canRemoveRow,
    [`${STEP_MAPPING_CLASS_NAME}__icon--edit`]: canEdit,
  });
};

const STEP_MAPPING_CLASS_NAME = 'dp-step-mapping-row';
export const MAPPING_ROW_PADDING = 0.3125; // rem 0.3125 is ~ 5px

export function StepMappingRow({ contextNode, path, showRemoveIcon }: IStepMappingRowProps) {
  const pathString = path.join(PROVIDER_NODE_PATH_SEPARATOR); // from p-grid, these are all comma separated, not sure why.
  const dragHasStarted = useMappingEditorStore(getDragTrulyStarted);
  const isNodeSelected = useMappingEditorStore(
    useCallback(
      state => {
        const draggingPath = draggingFromFlowPath(state);
        const hasDragStarted = getDragTrulyStarted(state);
        return (draggingPath === pathString && hasDragStarted) || getSelectedStepNodeId(state) === pathString;
      },
      [pathString]
    )
  );
  const mappingEditorId = useMappingEditorStore(storeIdSelector);
  const expandedNodes = useMappingEditorStore(getExpandedStepNodes);
  const isNodeHovered = useRowIsHovered(pathString, false);
  const isPartnerHovered = useRowPartnerIsHovered(pathString, false);

  // TODO move action onto one object to reduce this boilerplate, if it doesn't impact renders.
  const selectNode = useMappingEditorStore(state => state.selectNode);
  const expandNode = useMappingEditorStore(state => state.expandStepNode);
  const selectClear = useMappingEditorStore(state => state.selectClear);
  const hoverClear = useMappingEditorStore(state => state.hoverClear);
  const hoverNode = useMappingEditorStore(state => state.hoverNode);
  const startDragConnector = useMappingEditorStore(state => state.startDragConnector);
  const setRowHeights = useMappingEditorStore(state => state.setStepRowHeights);

  const validation = getParentValidationSummary(contextNode);
  const currentMapping = getMappingName(contextNode);
  const context = useContext(PropertyEditorContext);
  const prevMappingType = useRef(currentMapping);
  const previousHeight = useRef(0);
  /**
   * Work around a race condition between mouse-down focus and mouse up "click"
   * Alternative would be handling keyboard events manually instead of relying on tab focus.
   */
  const selectingByFocus = useRef(false);
  const fieldPath = getMappingFieldPath(contextNode, path);
  const typeDisplayName = getEditorConfigDisplayName(getMappingField(contextNode));
  const values = getMappingEditorValues(getMappingField(contextNode));
  const hasValidations = validation.reasons.length > 0;
  const elementId = getMappingElementId(pathString);
  const { validationTooltipId, validationAttributes } = getMappingValidationId(pathString, validation);
  const stepRow: MutableRefObject<HTMLDivElement | null> = useRef(null);
  const editorWrapper: MutableRefObject<HTMLDivElement | null> = useRef(null);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const linkRef: MutableRefObject<HTMLButtonElement | null> = useRef(null);
  const { isOpen, setIsOpen, toggleOpen } = HasOpenState();
  const childPath = getChildPath(path, contextNode);
  const canAdd = isBuildJoinArrayMapping(currentMapping);
  const initiallyOpen = getNodeIsInitiallyOpen(contextNode, currentMapping);
  const open = typeof expandedNodes?.get(pathString) === 'boolean' ? expandedNodes.get(pathString) : initiallyOpen;
  const isBuildData = currentMapping === InputMappingConstants.MAPPING_BUILD;
  const valueNode = getMappedValueNode(contextNode);
  const canEditBuildData = isBuildDataWithProperties(isBuildData, valueNode);
  const isList = getEditorConfigValue(getMappingField(contextNode), 'isList');
  const isNodeContainsPropertyNode = containsPropertyNode(contextNode, currentMapping);
  const isPlainCategoryNode = !categoryIsMappingTypeSelector(contextNode);
  const [contextRegistrationId, setContextRegistrationId] = useState('');
  const isInvalidMapping = hasInvalidPathValidation(valueNode?.ValidationIssues);
  const convertActions =
    isInvalidMapping && getConverterFlowSuggestionActions(getEditorValues(getMappingField(contextNode)));
  const alwaysShowEditors = useMappingEditorStore(state => state.alwaysShowEditors);
  const isConstant = mappingTypeRendersInlineEditor(currentMapping);
  const isOverlayType = mappingTypeRendersInOverlay(currentMapping);
  const [hasFocusInside, setHasFocusInside] = useState(false);

  const showEditor = isConstant && (alwaysShowEditors || (isNodeSelected && !!valueNode));
  const showOverlayEditor = (isOverlayType || (!!contextRegistrationId && canEditBuildData)) && isNodeSelected;

  if (canAdd) {
    const deleteAll = { label: 'Delete All', value: `${currentMapping}+clear` };
    values.current?.push(deleteAll);
  }

  const onMappingSelect = useCallback(
    async (value: string) => {
      if (!value) return;
      setIsOpen(false);
      prevMappingType.current = value;

      await context.onChange(value, fieldPath); // wait for this before expanding rows.

      if (mappingTypeRendersInlineEditor(value)) {
        !isNodeSelected && selectNode(pathString, MappingPanelType.STEP);
      }
      if (isBuildJoinArrayMapping(value) && !open) {
        expandNode(pathString);
      }
    },
    [setIsOpen, open, expandNode, pathString, context, fieldPath, isNodeSelected, selectNode]
  );

  const actionLayerClose = useCallback(() => {
    if (isNodeHovered) hoverClear();
    toggleOpen();
  }, [isNodeHovered, hoverClear, toggleOpen]);

  // debounce the showing of the tooltip, so there's a little time for mouse-movement
  // between hovered row icon and the tooltip
  const debouncedSetIsTooltipOpen = useMemo(() => debounce(setIsTooltipOpen, 300), []);
  useEffect(() => {
    debouncedSetIsTooltipOpen.clear();
    debouncedSetIsTooltipOpen(isNodeHovered);
  }, [debouncedSetIsTooltipOpen, isNodeHovered]);

  useEffect(() => {
    if (selectingByFocus.current && (!isNodeSelected || !isConstant)) {
      selectingByFocus.current = false;
    }
  }, [isConstant, isNodeHovered, isNodeSelected]);

  useLayoutEffect(() => {
    if (previousHeight.current === stepRow.current?.offsetHeight) {
      return; // don't re-index if we don't need to.
    }

    updateMappingRowHeights(mappingEditorId, setRowHeights);
    previousHeight.current = stepRow.current?.offsetHeight || 0;
  });

  useEffect(() => {
    // also update these when the rows are unmounted (regardless of layout)
    return () => {
      updateMappingRowHeights(mappingEditorId, setRowHeights);
    };
  }, [mappingEditorId, setRowHeights]);

  const handlePickButtonClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      toggleOpen();
    },
    [toggleOpen]
  );

  const onEditorLayerClose = useCallback(async () => {
    if (isNodeSelected) {
      selectClear();
    }
    if (canEditBuildData) {
      await editComplete({
        contextProviderRegistrationId: contextRegistrationId,
        completeType: EditCompleteType.Saved,
      });
    }
    setContextRegistrationId('');
  }, [isNodeSelected, selectClear, canEditBuildData, contextRegistrationId]);

  const onEdit = useCallback(async () => {
    if (isNodeContainsPropertyNode) return;
    const registrationId = await createContextForProperty({
      contextProviderRegistrationId: context.editingContextId,
      path: [...path, valueNode.Node.Name],
    });
    setContextRegistrationId(registrationId);
  }, [context.editingContextId, isNodeContainsPropertyNode, path, valueNode]);

  const onRowClick = useCallback(
    async (e?: React.MouseEvent) => {
      if (selectingByFocus.current || mouseTargetIsEditorOrOverlay(stepRow.current, editorWrapper.current, e?.target)) {
        return;
      }

      if (canEditBuildData && !contextRegistrationId) {
        // it's selected, probably prior to mapping change, but we didn't throw open the editor overlay immediately:
        await onEdit();
        !isNodeSelected && selectNode(pathString, MappingPanelType.STEP);
      } else {
        selectNode(pathString, MappingPanelType.STEP);
      }

      if (isConstant || isNodeContainsPropertyNode || canEditBuildData) {
        hoverClear();
      }
    },
    [
      canEditBuildData,
      contextRegistrationId,
      isConstant,
      isNodeContainsPropertyNode,
      onEdit,
      isNodeSelected,
      selectNode,
      pathString,
      hoverClear,
    ]
  );

  const onRowMouseEnter = () => {
    if (isPlainCategoryNode) {
      // it's not a mapping node. nothing to do here.
      return;
    }
    if (!isNodeSelected || dragHasStarted) {
      hoverNode(pathString, MappingPanelType.STEP);
    }
  };

  const onRowMouseLeave = () => {
    if (isNodeHovered && !convertActions) {
      hoverClear();
    }
  };

  const rowFocus = (e: React.FocusEvent) => {
    if (stepRow.current !== e.target) {
      // because React conflates focus and blur events (which do not bubble) with focusin and focusout events (which do)
      // we need to avoid calling this 2x when we are
      return;
    }

    !isNodeSelected && isConstant && !alwaysShowEditors && onRowClick();
    selectingByFocus.current = true;
  };

  const onBlur = (e: React.FocusEvent) => {
    if (alwaysShowEditors) {
      return; // we are just letting editor tab indexes do their things.
    }

    if (stepRow.current !== e.target) {
      // have to check the target, because react onBlur uses focusout under the hood.
      selectingByFocus.current = false;
    }
    // seems to be working just fine without this :eyes:
    // otherwise select the next node via the data store.
  };

  const onExpandClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      expandNode(pathString, !open);
    },
    [expandNode, open, pathString]
  );

  const onAddButtonClick = useCallback(() => {
    context.onChange(getBuildJoinArraysMappingValue(currentMapping), fieldPath);
  }, [context, fieldPath, currentMapping]);

  const onRemoveButtonClick = useCallback(
    (e: React.MouseEvent) => {
      // make sure this event doesn't bubble to other handlers at the row level,
      // e.g. calling `createContextForProperty` for a row in process of being removed.
      e.stopPropagation();
      // remove the row:
      context.onChange('+remove', fieldPath);
    },
    [context, fieldPath]
  );

  const onDrag = (e: React.MouseEvent) => {
    if (isPlainCategoryNode || mouseTargetIsEditorOrOverlay(stepRow.current, editorWrapper.current, e.target)) {
      // it's just a category, and we can't drag a connection to a category that's not a mapping
      // could use categoryIsMappingTypeSelector() util if this isn't robust enough, but should be fine.
      return;
    }
    // TODO think about timers here, or tracking mouse movement, vs just starting drag immediately on mouse-down
    // we have a `useDragAndDrop` we could think about using.
    startDragConnector(pathString, MappingPanelType.STEP);
  };

  return (
    <>
      <div
        className={getRowClassNames(
          STEP_MAPPING_CLASS_NAME,
          isNodeSelected,
          isNodeHovered || isPartnerHovered,
          isConstant,
          alwaysShowEditors
        )}
        id={elementId}
        ref={stepRow}
        onMouseEnter={onRowMouseEnter}
        onFocus={rowFocus}
        onBlur={onBlur}
        onMouseLeave={onRowMouseLeave}
        onClick={onRowClick}
        onMouseDown={onDrag}
        style={{ paddingLeft: `${path.length - 1 || MAPPING_ROW_PADDING}rem` }}
        {...validationAttributes}
        tabIndex={getRowTabIndex(isConstant, alwaysShowEditors, hasFocusInside)}
      >
        <MappingRowExpandIcon
          showIcon={contextNode.Children.some(child => child.Children?.length)}
          onClick={onExpandClick}
          open={open}
        />
        <div className={`${STEP_MAPPING_CLASS_NAME}__content-holder`}>
          <div className={`${STEP_MAPPING_CLASS_NAME}__content`}>
            <div
              className={getItemClassNames(`${STEP_MAPPING_CLASS_NAME}__label`, hasValidations)}
              title={contextNode.Node.Name}
            >
              {contextNode.Node.Name}
            </div>
            <MappingRowDataType dataType={isList ? `List of ${typeDisplayName}` : typeDisplayName} />
            {currentMapping && <PickButton onClick={handlePickButtonClick} text={currentMapping} buttonRef={linkRef} />}
            {canAdd && (
              <IconButton
                iconClassName={getIconClassNames(canAdd, false)}
                onClick={onAddButtonClick}
                iconSize={'normal'}
                aria-label="Add mapping"
              />
            )}
            {showRemoveIcon && (
              <IconButton
                iconClassName={getIconClassNames(false, showRemoveIcon)}
                onClick={onRemoveButtonClick}
                iconSize={'normal'}
                aria-label="Remove mapping"
              />
            )}
            {(canEditBuildData || isNodeContainsPropertyNode) && (
              <IconButton
                iconClassName={getIconClassNames(
                  canAdd,
                  showRemoveIcon,
                  canEditBuildData || isNodeContainsPropertyNode
                )}
                iconSize={'normal'}
                aria-label="Edit mapping"
              />
            )}
          </div>
          {!showEditor && (
            <StepMappedValueText
              className={`${STEP_MAPPING_CLASS_NAME}__mapping-value`}
              node={contextNode}
              currentMapping={currentMapping}
            />
          )}
          {showEditor && (
            <StepMappingEditor
              ref={editorWrapper}
              isEditorPanelOpen={showEditor}
              rowRef={stepRow}
              onClose={onEditorLayerClose}
              setFocusIn={setHasFocusInside}
              contextNode={valueNode}
              path={childPath}
              isForBuildData={canEditBuildData}
              editingContextId={contextRegistrationId}
              autoFocus={!alwaysShowEditors}
            />
          )}
        </div>
        <ValidationError
          id={validationTooltipId}
          sourceRef={stepRow.current ?? undefined}
          reasons={validation.reasons}
          levels={validation.levels}
          role="tooltip"
          worstLevel={validation.worstLevel}
          isOpen={hasValidations && isTooltipOpen}
          showTooltipAtCenter={true}
          validationTextClassName="dp-mapping-row__label--error"
          placement={'bottom'}
          hideErrorIcon={true}
          solutions={convertActions && <ActionLinks values={convertActions} callback={onMappingSelect} />}
          onMouseLeave={hoverClear}
        />
        <Callout isOpen={isOpen} onClose={actionLayerClose} sourceRef={linkRef} placement="left" showArrow={true}>
          <ActionLinks values={values} callback={onMappingSelect} />
        </Callout>
        {/* TODO editors and build data probably need to be split out at this point. */}
        {showOverlayEditor && (
          <StepMappingEditor
            isEditorPanelOpen={showOverlayEditor}
            rowRef={stepRow}
            onClose={onEditorLayerClose}
            contextNode={valueNode}
            isOverlayType={true}
            path={childPath}
            isForBuildData={canEditBuildData}
            editingContextId={contextRegistrationId}
          />
        )}
      </div>
      {open && (
        <section className={`${STEP_MAPPING_CLASS_NAME}__children`}>
          {Array.from(contextNode.Children.values()).map(
            child =>
              child.Children && (
                <StepMappingRow
                  key={child.Node.Name}
                  contextNode={child}
                  path={[...path, child.Node.Name]}
                  showRemoveIcon={canAdd} // if mapping of parent node is buildArray or Join Array we need to show remove icon
                />
              )
          )}
        </section>
      )}
    </>
  );
}

function isBuildDataWithProperties(isBuildDataMapping: boolean, child: ContextProviderNode): boolean {
  return isBuildDataMapping && child.Node.__type !== NodeTypes.Category;
}

/**
 * This is necessary because React (at least React 17) handles event bubbling (also?) in terms of react component
 * hierarchy, not just HTML element hierarchy. This creates some weirdness and difficultly with keeping these
 * mouse events from triggering handlers on the row element, when they might be sourced from the editor, or even
 * a dialog of that editor.
 *
 * Attempts to keep these events from bubbling with `e.stopPropagation` had other negative side-effects.
 *
 * @param rowElement mapping row
 * @param editorWrapper container around inline editors
 * @param eventTarget target of mouse event
 * @returns true if it should not be considered an event for the row element
 */
function mouseTargetIsEditorOrOverlay(
  rowElement: HTMLDivElement | null,
  editorWrapper: HTMLDivElement | null,
  eventTarget?: EventTarget
) {
  if (!rowElement || !eventTarget) {
    return false;
  }
  const isEditor = eventTarget === editorWrapper;
  const isEditorChild = editorWrapper?.contains(eventTarget as HTMLElement);
  const isNotEvenRowChild = rowElement.contains(eventTarget as HTMLElement) === false;

  if (isEditor || isEditorChild || isNotEvenRowChild) {
    return true;
  }
  return false;
}

/**
 * Get the tab index for a particular row at a particular time.
 * @param isConstant we only need tab indexes on constant rows
 * @param alwaysShowEditors we only need tab indexes on rows if they are not always expanded
 * @param childHasFocus we only need tab index on a row if its child doesn't have focus.
 * If its child has focus, we want `shift + tab` to go back to the previous row, not from the editor to it's parent row element
 * @returns a number or undefined
 */
function getRowTabIndex(isConstant: boolean, alwaysShowEditors: boolean, childHasFocus: boolean) {
  return isConstant && !alwaysShowEditors && !childHasFocus ? 1 : undefined;
}
